import { useCallback, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useAuth } from '../auth/AuthProvider';
import { Field } from '../components/Field';
import { Spinner } from '../components/Spinner';
import { defaultPhoneCountry } from '../config';
import {
  createClient,
  deleteClient,
  findClientsByEmail,
  getClient,
  updateClient,
  type Client,
} from '../lib/catalogue';
import { formatDateTime } from '../lib/dates';
import { ConflictError, errorMessage } from '../lib/errors';
import { useLoaded } from '../lib/hooks';
import { validateClient, type ClientInput, type FieldErrors } from '../lib/validation';
import { VehiclesSection } from './VehiclesSection';

export function ClientPage() {
  const { id } = useParams();
  return id ? <ExistingClient id={id} /> : <NewClient />;
}

function NewClient() {
  const navigate = useNavigate();
  return (
    <section>
      <Breadcrumb title="New client" />
      <h1>New client</h1>
      <ClientForm
        onSaved={(id) => navigate(`/clients/${id}`, { replace: true, state: { created: true } })}
      />
    </section>
  );
}

function ExistingClient({ id }: { id: string }) {
  const location = useLocation();
  const justCreated = Boolean((location.state as { created?: boolean } | null)?.created);
  const load = useCallback(() => getClient(id), [id]);
  const { loading, value: client, error, reload } = useLoaded(id, load);
  const [saved, setSaved] = useState(false);
  const { state } = useAuth();
  const navigate = useNavigate();
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');

  async function remove(c: Client) {
    const name = `${c.firstName} ${c.lastName}`;
    if (!window.confirm(`Remove ${name} and all their vehicles? This cannot be undone.`)) return;
    setRemoveError('');
    setRemoving(true);
    try {
      await deleteClient(c.id);
      navigate('/clients', { replace: true, state: { removed: name } });
    } catch (err) {
      setRemoveError(errorMessage(err));
      setRemoving(false);
    }
  }

  if (loading) return <Spinner />;
  if (!client) {
    return (
      <section>
        <Breadcrumb title="Client" />
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      </section>
    );
  }

  return (
    <section>
      <Breadcrumb title={`${client.firstName} ${client.lastName}`} />
      <div className="page-head">
        <h1>
          {client.firstName} {client.lastName}
        </h1>
        {/* The rules allow only admins to delete a client. */}
        {state.role === 'admin' && (
          <button type="button" className="btn btn--danger-solid" disabled={removing} onClick={() => void remove(client)}>
            {removing ? 'Removing…' : 'Remove client'}
          </button>
        )}
      </div>
      {removeError && (
        <p className="alert alert--error" role="alert">
          {removeError}
        </p>
      )}
      {justCreated && !saved && (
        <p className="alert alert--success" role="status">
          Client created. Add their vehicles below.
        </p>
      )}
      {saved && (
        <p className="alert alert--success" role="status">
          Changes saved.
        </p>
      )}
      <ClientForm
        // Re-mount with fresh values whenever the stored record changes.
        key={client.updatedAt.toMillis()}
        client={client}
        onSaved={() => {
          setSaved(true);
          reload();
        }}
        onReload={reload}
      />
      <AuditLine client={client} />
      <VehiclesSection clientId={client.id} />
    </section>
  );
}

function Breadcrumb({ title }: { title: string }) {
  return (
    <nav className="breadcrumb" aria-label="Breadcrumb">
      <Link to="/clients">Clients</Link> <span aria-hidden="true">/</span> <span>{title}</span>
    </nav>
  );
}

function AuditLine({ client }: { client: Client }) {
  return (
    <p className="audit muted">
      Created {formatDateTime(client.createdAt.toDate())} by {client.createdByEmail}
      {' · '}
      Last updated {formatDateTime(client.updatedAt.toDate())} by {client.updatedByEmail}
    </p>
  );
}

interface ClientFormProps {
  client?: Client;
  onSaved: (id: string) => void;
  onReload?: () => void;
}

function ClientForm({ client, onSaved, onReload }: ClientFormProps) {
  const [values, setValues] = useState<ClientInput>({
    firstName: client?.firstName ?? '',
    lastName: client?.lastName ?? '',
    email: client?.email ?? '',
    phone: client?.phone ?? '',
  });
  const [errors, setErrors] = useState<FieldErrors<ClientInput>>({});
  const [formError, setFormError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [duplicates, setDuplicates] = useState<Client[]>([]);
  const [duplicateOk, setDuplicateOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (name: keyof ClientInput) => (e: ChangeEvent<HTMLInputElement>) => {
    setValues((v) => ({ ...v, [name]: e.target.value }));
    if (name === 'email') {
      setDuplicates([]);
      setDuplicateOk(false);
    }
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError('');
    setConflict(false);

    const result = validateClient(values, defaultPhoneCountry);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    const fields = result.value;

    setBusy(true);
    try {
      if (!duplicateOk && fields.email !== client?.email) {
        const others = await findClientsByEmail(fields.email, client?.id);
        if (others.length > 0) {
          setDuplicates(others);
          return;
        }
      }
      if (client) {
        await updateClient(client.id, fields, client.updatedAt);
        onSaved(client.id);
      } else {
        onSaved(await createClient(fields));
      }
    } catch (err) {
      setConflict(err instanceof ConflictError);
      setFormError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card form-grid" onSubmit={submit} noValidate>
      <Field
        label="First name"
        name="firstName"
        autoComplete="off"
        value={values.firstName}
        onChange={set('firstName')}
        error={errors.firstName}
      />
      <Field
        label="Last name"
        name="lastName"
        autoComplete="off"
        value={values.lastName}
        onChange={set('lastName')}
        error={errors.lastName}
      />
      <Field
        label="Email"
        name="email"
        type="email"
        autoComplete="off"
        value={values.email}
        onChange={set('email')}
        error={errors.email}
      />
      <Field
        label="Phone"
        name="phone"
        type="tel"
        autoComplete="off"
        value={values.phone}
        onChange={set('phone')}
        error={errors.phone}
        hint={`Numbers without a +country code are read as ${defaultPhoneCountry}.`}
      />

      {duplicates.length > 0 && !duplicateOk && (
        <div className="alert alert--warning form-grid__full" role="alert">
          <p>
            Another client already uses this email:{' '}
            {duplicates.map((d, i) => (
              <span key={d.id}>
                {i > 0 && ', '}
                <Link to={`/clients/${d.id}`}>
                  {d.firstName} {d.lastName}
                </Link>
              </span>
            ))}
            .
          </p>
          <button type="button" className="btn" onClick={() => setDuplicateOk(true)}>
            It's a different person, allow it
          </button>
        </div>
      )}

      {formError && (
        <div className="alert alert--error form-grid__full" role="alert">
          <p>{formError}</p>
          {conflict && onReload && (
            <button type="button" className="btn" onClick={onReload}>
              Reload
            </button>
          )}
        </div>
      )}

      <div className="actions form-grid__full">
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? 'Saving…' : client ? 'Save changes' : 'Create client'}
        </button>
        {!client && (
          <Link to="/clients" className="btn btn--ghost">
            Cancel
          </Link>
        )}
      </div>
    </form>
  );
}
